import React from "react";

const STORAGE_PREFIX = "wwg.orderManagement.personId.";

// The BFF returns the people on an IBO account as `users`
// (GET /v1/ibos/:id → { users: [{ id, name, role, email }] }); some older
// frontend mocks still call the array `people`. Normalize here so every
// consumer sees one shape.
export const getIboPeople = (ibo) => {
  if (Array.isArray(ibo?.users)) return ibo.users;
  if (Array.isArray(ibo?.people)) return ibo.people;
  return [];
};

const readStored = (key) => {
  if (typeof window === "undefined" || !key) {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStored = (key, value) => {
  if (typeof window === "undefined" || !key) {
    return;
  }
  try {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, String(value));
    }
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};

// Tracks which person on a shared (two-person) IBO account the rep is acting
// as, persisted to localStorage per IBO so the choice survives tab switches,
// page navigation, and refreshes. Returns [selectedPersonId, setSelectedPersonId].
export function usePersistedPersonId(selectedIbo) {
  const iboNumber = selectedIbo?.iboNumber ?? null;
  const people = React.useMemo(() => getIboPeople(selectedIbo), [selectedIbo]);

  const storageKey = iboNumber ? `${STORAGE_PREFIX}${iboNumber}` : null;

  const [selectedPersonId, setSelectedPersonId] = React.useState(null);

  // When the IBO changes, restore the persisted choice if it's still valid,
  // otherwise fall back to the first person.
  React.useEffect(() => {
    if (!people.length) {
      setSelectedPersonId(null);
      return;
    }
    const stored = readStored(storageKey);
    const match = stored && people.find((p) => String(p.id) === stored);
    setSelectedPersonId(match ? match.id : people[0]?.id ?? null);
  }, [storageKey, people]);

  const selectPerson = React.useCallback(
    (personId) => {
      setSelectedPersonId(personId);
      writeStored(storageKey, personId);
    },
    [storageKey]
  );

  return [selectedPersonId, selectPerson];
}

export default usePersistedPersonId;
